/**
 * 6.D weekly hours editor (`div.eb-weekly-list.rich-weekly`) — 7 rows Monday→Sunday.
 * Pure: receives the rows and returns new rows via `onChange`; the caller decides where they live
 * (event row or a staff row). Interval errors come from validation (`{ "day:{id}:{idx}": message }`).
 */

import React, { useEffect, useRef, useState } from "react";
import { WEEKDAYS } from "../../state/constants";
import { Switch, FieldError } from "../../ui/primitives";
import { addIntervalRows, copyDayRows, removeIntervalRows, setIntervalRows, toggleDayRows } from "../shared/useScheduleOps";

const CopyDaysPopover = ({ fromDayId, onApply, onClose }) => {
  const [checked, setChecked] = useState(() => WEEKDAYS.filter((d) => d.id !== fromDayId).map((d) => d.id));
  const ref = useRef(null);
  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);
  const toggle = (id) => setChecked((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id]));
  return (
    <div className="eb-copy-pop" ref={ref} role="dialog" aria-label="Copy hours to other days">
      {WEEKDAYS.filter((d) => d.id !== fromDayId).map((d) => (
        <label key={d.id}>
          <input type="checkbox" checked={checked.includes(d.id)} onChange={() => toggle(d.id)} />
          {d.name}
        </label>
      ))}
      <div>
        <button type="button" className="eb-outline-small" onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="eb-outline-small" onClick={() => onApply(checked)} disabled={!checked.length}>
          Apply
        </button>
      </div>
    </div>
  );
};

const WeeklyList = ({ rows, onChange, readOnly, errors = {}, errorPrefix = "", id }) => {
  const [copyFor, setCopyFor] = useState(null);
  const change = (next) => !readOnly && onChange(next);

  return (
    <div className={`eb-weekly-list rich-weekly ${readOnly ? "is-readonly" : ""}`.trim()} id={id}>
      {WEEKDAYS.map((day) => {
        const row = (rows || []).find((r) => !r.dated && r.dayId === day.id);
        const on = Boolean(row);
        const key = { dayId: day.id };
        const intervals = row ? row.schedules || [] : [];
        return (
          <div key={day.id} className="eb-row-anchor">
            <Switch on={on} disabled={readOnly} label={`Toggle ${day.name}`} onChange={(v) => change(toggleDayRows(rows, day.id, v))} />
            <strong>{day.name}</strong>
            {!on ? (
              <em>Unavailable</em>
            ) : (
              <div className="eb-interval-lines">
                {intervals.map((slot, idx) => {
                  const err = errors[`${errorPrefix}day:${day.id}:${idx}`];
                  return (
                    <React.Fragment key={idx}>
                      <div className="eb-interval-line">
                        <input
                          type="time"
                          step="900"
                          value={slot.start_time || ""}
                          disabled={readOnly}
                          aria-label={`${day.name} start time`}
                          className={err ? "eb-has-error" : ""}
                          onChange={(e) => change(setIntervalRows(rows, key, idx, { start_time: e.target.value }))}
                        />
                        <span>-</span>
                        <input
                          type="time"
                          step="900"
                          value={slot.end_time || ""}
                          disabled={readOnly}
                          aria-label={`${day.name} end time`}
                          className={err ? "eb-has-error" : ""}
                          onChange={(e) => change(setIntervalRows(rows, key, idx, { end_time: e.target.value }))}
                        />
                        {idx === 0 ? (
                          <>
                            <button type="button" className="eb-row-icon" title="Add hours" disabled={readOnly} onClick={() => change(addIntervalRows(rows, key))}>
                              +
                            </button>
                            <button
                              type="button"
                              className="eb-row-icon"
                              title="Copy to other days"
                              disabled={readOnly}
                              aria-expanded={copyFor === day.id}
                              onClick={() => setCopyFor(copyFor === day.id ? null : day.id)}
                            >
                              {"\u2907"}
                            </button>
                          </>
                        ) : (
                          <>
                            <span />
                            <button type="button" className="eb-row-icon" title="Remove hours" disabled={readOnly} onClick={() => change(removeIntervalRows(rows, key, idx))}>
                              {"\u2212"}
                            </button>
                          </>
                        )}
                      </div>
                      <FieldError message={err} />
                    </React.Fragment>
                  );
                })}
              </div>
            )}
            {copyFor === day.id ? (
              <CopyDaysPopover
                fromDayId={day.id}
                onClose={() => setCopyFor(null)}
                onApply={(targets) => {
                  change(copyDayRows(rows, day.id, targets));
                  setCopyFor(null);
                }}
              />
            ) : null}
          </div>
        );
      })}
    </div>
  );
};

export default WeeklyList;
